import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui/skeleton';

interface QuickStatProps {
  label: string;
  hint?: string;
  value: ReactNode;
  suffix?: ReactNode;
  subMeta?: ReactNode;
  chart: ReactNode;
  icon?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function QuickStat({
  label,
  hint,
  value,
  suffix,
  subMeta,
  chart,
  icon,
  loading = false,
  className,
}: QuickStatProps) {
  return (
    <article
      aria-label={label}
      aria-busy={loading}
      className={cn(
        'surface-card-hover flex min-w-0 flex-col gap-4 rounded-2xl border border-line-1 bg-bg-1 p-3 shadow-card sm:p-5',
        className,
      )}
    >
      <header className="flex flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-text-2 sm:text-[12px]">
          {icon && (
            <span className="text-violet-deep" aria-hidden="true">
              {icon}
            </span>
          )}
          <span>{label}</span>
          {hint && <Info className="h-3 w-3 text-text-4" aria-label={hint} />}
        </div>
        {subMeta && <div className="text-[11px] text-text-4">{subMeta}</div>}
      </header>

      <div className="flex flex-wrap items-baseline gap-1.5">
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <div className="font-display break-all text-[30px] leading-none tracking-tight text-text-1 sm:text-[32px]">
            {value}
          </div>
        )}
        {!loading && suffix && <div className="text-[13px] text-text-3">{suffix}</div>}
      </div>

      <div className="mt-auto border-t border-line-1/60 pt-3">
        {loading ? <Skeleton className="h-9 w-full" /> : chart}
      </div>
    </article>
  );
}
